import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Toaster } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BrandBand from "@/components/BrandBand";
import ProtectedRoute from "@/components/ProtectedRoute";
import SeasonalBackdrop from "@/components/SeasonalBackdrop";
import AuthCallback from "@/components/AuthCallback";

import Home from "@/pages/Home";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogDetail from "@/pages/BlogDetail";
import Contact from "@/pages/Contact";
import MyLibrary from "@/pages/MyLibrary";
import ColoringPage from "@/pages/ColoringPage";
import ColoringGallery from "@/pages/ColoringGallery";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MagicVerify from "@/pages/MagicVerify";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminBooks from "@/pages/AdminBooks";
import AdminBlog from "@/pages/AdminBlog";
import AdminSubscribers from "@/pages/AdminSubscribers";
import AdminContacts from "@/pages/AdminContacts";
import AdminPurchases from "@/pages/AdminPurchases";
import AdminComments from "@/pages/AdminComments";
import AdminNewsletter from "@/pages/AdminNewsletter";
import ShareCard from "@/pages/ShareCard";

function PublicLayout({ children }) {
  return (
    <div className="App relative">
      <SeasonalBackdrop />
      <Navbar />
      <main style={{ position: "relative", zIndex: 1 }}>{children}</main>
      <BrandBand />
      <Footer />
    </div>
  );
}

/**
 * AppRouter checks the URL hash SYNCHRONOUSLY (during render) for a
 * session_id fragment coming back from Emergent OAuth. If present, we
 * render AuthCallback BEFORE the normal routes to avoid race conditions
 * with the global AuthProvider `/auth/me` check.
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/books" element={<PublicLayout><Books /></PublicLayout>} />
      <Route path="/books/:id" element={<PublicLayout><BookDetail /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogDetail /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
      <Route path="/my/library" element={<PublicLayout><MyLibrary /></PublicLayout>} />
      <Route path="/coloring" element={<PublicLayout><ColoringGallery /></PublicLayout>} />
      <Route path="/coloring/:bookId" element={<PublicLayout><ColoringPage /></PublicLayout>} />
      <Route path="/payment/success" element={<PublicLayout><PaymentSuccess /></PublicLayout>} />
      <Route path="/payment/cancel" element={<PublicLayout><PaymentCancel /></PublicLayout>} />
      <Route path="/auth/magic" element={<PublicLayout><MagicVerify /></PublicLayout>} />

      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Bare route used to render Instagram share cards at 1080x1080 — no navbar/footer */}
      <Route path="/share/instagram/:id" element={<ShareCard />} />
      <Route path="/share/:format/:id" element={<ShareCard />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="books" element={<AdminBooks />} />
        <Route path="posts" element={<AdminBlog />} />
        <Route path="subscribers" element={<AdminSubscribers />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="purchases" element={<AdminPurchases />} />
        <Route path="comments" element={<AdminComments />} />
        <Route path="newsletter" element={<AdminNewsletter />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FavoritesProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#FFFFFF",
                color: "#3D405B",
                border: "1px solid #E8E2D2",
                borderRadius: "1rem",
                fontFamily: "'Nunito', sans-serif",
              },
            }}
          />
          <AppRouter />
        </BrowserRouter>
      </FavoritesProvider>
    </AuthProvider>
  );
}
